import { keepPreviousData } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { isScanAnalysisInProgress, useFindingQuickViewState, useGetScanLifecycleState } from '../../hooks/scan-hooks.ts'
import { useGetScaleFromHalLink, useGetScan, useGetScanFindings } from '../../utils/api-client/api-hooks.ts'
import { getSeveritiesByRankDescending } from '../../utils/scale-utils.ts'
import {
  DEFAULT_INITIAL_STATE,
  mapFilterStateToGetScanFindingsParams,
  selectedFilterKeys,
  useFilterStateStore,
} from '../tables/filter-state-store.tsx'
import { getFindingQuickViewConfig } from '../tables/finding-quick-view-config'
import type { Categorization } from './resolution-categorization.ts'
import { toResolutionPanelData } from './resolution-panel-data.ts'
import {
  getIsResolutionPanelExpanded,
  getStoredCategorization,
  getStoredResolutionScope,
  setIsResolutionPanelExpanded,
  setStoredCategorization,
  setStoredResolutionScope,
} from './resolution-panel-expansion.ts'
import { areSelectionsEqual, toActiveResolutionFilter, toCombinedFilterSelection } from './resolution-panel-filters.ts'
import { isScopeEmpty, toScopeFilters, type ResolutionScope } from './resolution-scope.ts'
import {
  ANALYZED_FILTER,
  getEmptyResolutionCounts,
  type ResolutionFilter,
  type ResolutionPanelData,
} from './resolution-taxonomy.ts'

const EMPTY_PANEL_DATA: ResolutionPanelData = {
  resolution: getEmptyResolutionCounts(),
  inProgressCount: 0,
  unanalyzedCount: 0,
}

/**
 * Loads the counts the Resolution panel draws and connects its selections to the findings table.
 *
 * Three queries, each asking for one row since only the aggregate counts are read: the view's
 * analyzed findings, the same narrowed to merged patches (the merged-patch count has no field in
 * the statistics), and the scan's findings not yet fully analyzed. All three cover the whole quick
 * view regardless of the filters the user has layered on, and all three follow the Data scope.
 */
export const useResolutionPanel = ({ scanId }: { scanId: string }) => {
  const { selectedQuickView } = useFindingQuickViewState({ scanId })
  const { scanLifecycleState } = useGetScanLifecycleState({ scanId })
  const { data: scan } = useGetScan({ id: scanId })
  const { data: scale } = useGetScaleFromHalLink({ path: scan?._links?.scale?.href })
  // The collapsed bar shows a pulsing "N analyzing" counter and the funnel shimmers its in-progress
  // row, so the counts behind them have to move too. Same cadence as the findings table above which
  // this panel sits.
  const refetchInterval = isScanAnalysisInProgress(scanLifecycleState) ? 1000 : false
  const quickViewConfig = getFindingQuickViewConfig(selectedQuickView)

  const [scope, setScope] = useState(getStoredResolutionScope)
  const [categorization, setCategorization] = useState(getStoredCategorization)

  const onScopeChange = useCallback((nextScope: ResolutionScope) => {
    setStoredResolutionScope(nextScope)
    setScope(nextScope)
  }, [])

  const onCategorizationChange = useCallback((nextCategorization: Categorization) => {
    setStoredCategorization(nextCategorization)
    setCategorization(nextCategorization)
  }, [])

  // Written to the store so the findings table narrows to the scope: its query reads the live store
  // state, which `mapFilterStateToGetScanFindingsParams` intersects the scope into. The panel's own
  // queries are built from the quick view's defaults rather than the store, so they add the same
  // scope filters below — otherwise the table would narrow while the panel kept counting everything.
  //
  // Derived here because inverting a severity exclusion needs the scan's severity scale, which the
  // store has no access to. Until the scale loads there are no severity labels to invert against,
  // so a severity exclusion has no effect for that first render rather than excluding everything.
  const severityLabels = useMemo(() => getSeveritiesByRankDescending(scale).map(severity => severity.label), [scale])
  const scopeFilters = useMemo(() => toScopeFilters({ scope, severityLabels }), [scope, severityLabels])
  const setScopeFilters = useFilterStateStore(state => state.setScopeFilters)

  useEffect(() => {
    setScopeFilters(scopeFilters)
  }, [scopeFilters, setScopeFilters])

  const defaults = quickViewConfig.defaultFilterState
  const isAllFindingsView = quickViewConfig.id === 'all-findings'
  const isUnanalyzedView = quickViewConfig.id === 'unanalyzed'

  // The panel splits the view by the same `analyzed` predicate the findings table filters by, so a
  // clicked row's count is the table's total. The analyzed set is the quick view narrowed to fully
  // analyzed findings — every narrower SAST view already is — and the not-analyzed set is its
  // complement across the whole scan. The store drops the outcome part of the scope from the
  // not-analyzed request, since those findings have no outcomes, and keeps its severity part.
  const analyzedFilterState = isAllFindingsView ? { ...defaults, filterToAnalyzed: true } : defaults
  const notAnalyzedFilterState = { ...DEFAULT_INITIAL_STATE, filterToUnanalyzed: true }
  const analyzedParams = mapFilterStateToGetScanFindingsParams({ ...analyzedFilterState, scopeFilters })
  const notAnalyzedParams = mapFilterStateToGetScanFindingsParams({ ...notAnalyzedFilterState, scopeFilters })

  const applyResolutionSelection = useFilterStateStore(state => state.applyResolutionSelection)
  const setFilterToAnalyzed = useFilterStateStore(state => state.setFilterToAnalyzed)
  const classification = useFilterStateStore(state => state.classification)
  const fixResult = useFilterStateStore(state => state.fixResult)
  const patchStatus = useFilterStateStore(state => state.patchStatus)
  const filterToAnalyzed = useFilterStateStore(state => state.filterToAnalyzed)

  const {
    data: analyzedData,
    isError: isAnalyzedError,
    refetch: refetchAnalyzed,
  } = useGetScanFindings({
    scanId,
    pagination: { pageIndex: 0, pageSize: 1 },
    ...analyzedParams,
    enabled: !isUnanalyzedView,
    refetchInterval,
    placeholderData: keepPreviousData,
  })

  const {
    data: mergedData,
    isError: isMergedError,
    refetch: refetchMerged,
  } = useGetScanFindings({
    scanId,
    pagination: { pageIndex: 0, pageSize: 1 },
    ...analyzedParams,
    patchStatuses: ['merged'],
    enabled: !isUnanalyzedView,
    refetchInterval,
    placeholderData: keepPreviousData,
  })

  const {
    data: notAnalyzedData,
    isError: isNotAnalyzedError,
    refetch: refetchNotAnalyzed,
  } = useGetScanFindings({
    scanId,
    pagination: { pageIndex: 0, pageSize: 1 },
    ...notAnalyzedParams,
    refetchInterval,
    placeholderData: keepPreviousData,
  })

  // The scope's footnote needs each count before the exclusions were applied, which no other query
  // reports. They only run while a scope is set.
  const isScoped = !isScopeEmpty(scope)
  const { data: unscopedAnalyzedData } = useGetScanFindings({
    scanId,
    pagination: { pageIndex: 0, pageSize: 1 },
    ...mapFilterStateToGetScanFindingsParams(analyzedFilterState),
    enabled: isScoped && !isUnanalyzedView,
    refetchInterval,
    placeholderData: keepPreviousData,
  })
  const { data: unscopedNotAnalyzedData } = useGetScanFindings({
    scanId,
    pagination: { pageIndex: 0, pageSize: 1 },
    ...mapFilterStateToGetScanFindingsParams(notAnalyzedFilterState),
    enabled: isScoped && (isAllFindingsView || isUnanalyzedView),
    refetchInterval,
    placeholderData: keepPreviousData,
  })

  const hiddenAnalyzedByScopeCount =
    unscopedAnalyzedData && analyzedData ? Math.max(unscopedAnalyzedData.total - analyzedData.total, 0) : 0
  const hiddenNotAnalyzedByScopeCount =
    unscopedNotAnalyzedData && notAnalyzedData ? Math.max(unscopedNotAnalyzedData.total - notAnalyzedData.total, 0) : 0
  // What the scope removes from the findings this view counts: both sets in All findings, the
  // not-analyzed set in Unanalyzed, and the analyzed set in every narrower view.
  const hiddenByScopeCount = isAllFindingsView
    ? hiddenAnalyzedByScopeCount + hiddenNotAnalyzedByScopeCount
    : isUnanalyzedView
      ? hiddenNotAnalyzedByScopeCount
      : hiddenAnalyzedByScopeCount

  // Every query the composition is drawn from has to land before it is meaningful: `fixed` comes
  // from the merged count alone, so rendering early would count fixed findings as still needing
  // action until the second request caught up, then visibly shift.
  // An errored query also has no data, so the two are separated: a failed request should not leave
  // the panel loading forever.
  const isError = isAnalyzedError || isMergedError || isNotAnalyzedError
  // All of them, not just the one that failed: the composition is drawn from the set, so retrying
  // one would pair a fresh count with stale ones.
  const onRetry = useCallback(() => {
    void refetchAnalyzed()
    void refetchMerged()
    void refetchNotAnalyzed()
  }, [refetchAnalyzed, refetchMerged, refetchNotAnalyzed])
  const hasAnalyzedCounts = isUnanalyzedView || (!!analyzedData && !!mergedData)
  const isLoading = !isError && (!hasAnalyzedCounts || !notAnalyzedData)
  const data =
    hasAnalyzedCounts && notAnalyzedData
      ? toResolutionPanelData({
          statistics: isUnanalyzedView ? undefined : analyzedData,
          mergedCount: mergedData?.total ?? 0,
          notAnalyzedStatistics: notAnalyzedData,
        })
      : EMPTY_PANEL_DATA

  const [isExpanded, setIsExpanded] = useState(getIsResolutionPanelExpanded)
  // The persistence write stays out of the state updater: updaters must be pure, and Strict Mode
  // double-invokes them, which would double-fire the write for one toggle.
  const onToggleExpanded = useCallback(() => {
    const nextIsExpanded = !isExpanded
    setIsResolutionPanelExpanded(nextIsExpanded)
    setIsExpanded(nextIsExpanded)
  }, [isExpanded])

  // Derived from the store rather than remembered locally, so the highlight is correct however the
  // table came to be filtered — a click here, a filter restored from storage on load, or the
  // table's own controls clearing it.
  //
  // Recognised from the whole slice, because that is how `applyResolutionSelection` writes it —
  // subtracting the quick view's baseline here would never match what a click just applied.
  //
  // The one case the baseline still matters: a narrowed view such as "Dismissable findings" seeds
  // exactly the Dismissed selection, so while the store still equals that baseline there is no
  // panel selection to report.
  const activeFilter = useMemo(() => {
    const selection = {
      classifications: selectedFilterKeys(classification),
      fixResults: selectedFilterKeys(fixResult),
      patchStatuses: selectedFilterKeys(patchStatus),
    }
    const baseline = {
      classifications: selectedFilterKeys(defaults.classification),
      fixResults: selectedFilterKeys(defaults.fixResult),
      patchStatuses: selectedFilterKeys(defaults.patchStatus),
    }

    if (areSelectionsEqual(selection, baseline)) {
      // With no state selected, the only panel filter that can be on is Analyzed, which narrows
      // the All findings view by analysis state alone.
      return isAllFindingsView && filterToAnalyzed ? ANALYZED_FILTER : undefined
    }
    return toActiveResolutionFilter(selection)
  }, [classification, fixResult, patchStatus, defaults, isAllFindingsView, filterToAnalyzed])

  // Clearing goes through the same action as applying, so deselecting a segment releases only the
  // three dimensions the panel owns. `resetFilters` would also wipe severity, fix confidence and
  // the rest, which the user set somewhere else.
  //
  // It returns those slices to the quick view's baseline rather than to empty: narrowed views seed
  // classifications and disable that group in the dropdown, so emptying them would leave a state
  // the user could not restore.
  const onFilterChange = useCallback(
    (filter: ResolutionFilter | undefined) => {
      applyResolutionSelection(
        filter
          ? toCombinedFilterSelection(filter.states)
          : {
              classifications: selectedFilterKeys(defaults.classification),
              fixResults: selectedFilterKeys(defaults.fixResult),
              patchStatuses: selectedFilterKeys(defaults.patchStatus),
            }
      )
      // In All findings, every panel selection sits inside the analyzed set its count was drawn
      // from, so the table narrows to analyzed findings with it and releases that when cleared.
      if (isAllFindingsView) {
        setFilterToAnalyzed(!!filter)
      }
    },
    [applyResolutionSelection, defaults, isAllFindingsView, setFilterToAnalyzed]
  )

  return {
    data,
    isLoading,
    isError,
    onRetry,
    isExpanded,
    onToggleExpanded,
    activeFilter,
    onFilterChange,
    scope,
    onScopeChange,
    categorization,
    onCategorizationChange,
    hiddenByScopeCount,
    hiddenAnalyzedByScopeCount,
    statistics: analyzedData ?? notAnalyzedData,
    scale,
    quickViewId: quickViewConfig.id,
  }
}
